/**
 * AudioSourceManager — Handles audio capture from system audio or microphone.
 *
 * Primary source: navigator.mediaDevices.getDisplayMedia() with systemAudio
 *   → Captures system audio output (including Spotify desktop app)
 *   → DJ selects "Entire Screen" and checks "Share system audio"
 *   → We discard the video track immediately — only need audio
 *
 * Fallback source: navigator.mediaDevices.getUserMedia() with microphone
 *   → Captures ambient room audio
 *   → Works universally but lower quality for visualization
 */

import { AudioSourceType, AudioSourceStatus } from './types';

export class AudioSourceManager {
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private analyserNode: AnalyserNode | null = null;
  private sourceType: AudioSourceType = 'none';
  private onEndedCallback: (() => void) | null = null;

  /** Get the AnalyserNode for reading audio data */
  get analyser(): AnalyserNode | null {
    return this.analyserNode;
  }

  /** Get the AudioContext (needed to resume after user gesture) */
  get context(): AudioContext | null {
    return this.audioContext;
  }

  /** Current source status */
  getStatus(): AudioSourceStatus {
    return {
      type: this.sourceType,
      active: this.stream !== null && this.stream.getAudioTracks().some(t => t.readyState === 'live'),
      label: this.sourceType === 'system' ? 'System Audio'
           : this.sourceType === 'microphone' ? 'Microphone'
           : 'No Audio Source',
    };
  }

  /** Register callback for when audio capture ends (e.g., user stops sharing) */
  onEnded(callback: () => void): void {
    this.onEndedCallback = callback;
  }

  /**
   * Start capturing system audio via getDisplayMedia.
   * This triggers the browser's screen share picker — the DJ must select a screen
   * and check "Share system audio" (Windows).
   */
  async startSystemAudio(): Promise<void> {
    this.stop();

    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'monitor' } as MediaTrackConstraints,
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
      preferCurrentTab: false,  // hint Chrome to default to "Entire Screen" tab
      systemAudio: 'include',
    } as DisplayMediaStreamOptions);

    // Discard video track — we only need audio for visualization
    stream.getVideoTracks().forEach(track => track.stop());

    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) {
      throw new Error('No audio track captured. Make sure to check "Share system audio" when selecting the screen.');
    }

    // Listen for the track ending (user clicks "Stop sharing" in browser chrome)
    audioTracks[0].addEventListener('ended', () => {
      this.sourceType = 'none';
      this.onEndedCallback?.();
    });

    this.stream = stream;
    this.sourceType = 'system';
    this.connectToAnalyser(new MediaStream(audioTracks));
  }

  /**
   * Start capturing microphone audio as a fallback.
   * Useful when system audio capture isn't available or for external hardware setups.
   */
  async startMicrophone(): Promise<void> {
    this.stop();

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });

    stream.getAudioTracks()[0].addEventListener('ended', () => {
      this.sourceType = 'none';
      this.onEndedCallback?.();
    });

    this.stream = stream;
    this.sourceType = 'microphone';
    this.connectToAnalyser(stream);
  }

  /** Connect a MediaStream to the Web Audio API AnalyserNode */
  private connectToAnalyser(audioStream: MediaStream): void {
    this.audioContext = new AudioContext();
    this.sourceNode = this.audioContext.createMediaStreamSource(audioStream);
    this.analyserNode = this.audioContext.createAnalyser();

    // FFT size 2048 → 1024 frequency bins. Good balance of resolution and performance.
    this.analyserNode.fftSize = 2048;
    // smoothingTimeConstant: 0 = raw (jittery), 1 = frozen. 0.8 is a good default.
    this.analyserNode.smoothingTimeConstant = 0.8;

    this.sourceNode.connect(this.analyserNode);
    // Do NOT connect to audioContext.destination — we don't want to play audio through the browser
  }

  /** Stop all audio capture and clean up resources */
  stop(): void {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    if (this.analyserNode) {
      this.analyserNode.disconnect();
      this.analyserNode = null;
    }
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    this.sourceType = 'none';
  }
}
